import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import {
  useGetWorkflowDefinitionQuery,
  usePublishWorkflowDefinitionMutation,
  useCreateNewWorkflowVersionMutation,
  useRetireWorkflowDefinitionMutation,
  useDeleteWorkflowDefinitionMutation,
  WorkflowDefinition,
  StageDefinition,
  ProcessDefinition,
  StatusDefinition,
  TransitionDefinition,
  GovernanceGate,
  AutomationHook,
  AuthorityRule,
} from '@/redux/internal-services/workflowApi';

// ─────────────────────────────── Helpers ──────────────────────────────────

const STATUS_BADGE: Record<string, string> = {
  draft:     'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  published: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  archived:  'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const LIFECYCLE_BADGE: Record<string, string> = {
  entry:        'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  intermediate: 'bg-ui-subtle text-ui-text2',
  terminal:     'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const OP_BADGE: Record<string, string> = {
  normal:    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  on_hold:   'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  closed:    'bg-ui-subtle text-ui-text2',
  cancelled: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  rejected:  'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const TRANSITION_BADGE: Record<string, string> = {
  standard:        'bg-blue-100 text-blue-700',
  backward:        'bg-orange-100 text-orange-700',
  skip:            'bg-purple-100 text-purple-700',
  hold:            'bg-yellow-100 text-yellow-700',
  resume:          'bg-teal-100 text-teal-700',
  reopen:          'bg-indigo-100 text-indigo-700',
  cancel:          'bg-red-100 text-red-700',
  reject:          'bg-red-100 text-red-800',
  corrective_loop: 'bg-pink-100 text-pink-700',
};

const Chip: React.FC<{ label: string; className?: string }> = ({ label, className = '' }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${className}`}>
    {label.replace(/_/g, ' ')}
  </span>
);

const RolePill: React.FC<{ role: string }> = ({ role }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-brand-dark/10 text-brand-dark capitalize">
    {role.replace(/_/g, ' ')}
  </span>
);

const Section: React.FC<{ title: string; count: number; children: React.ReactNode }> = ({
  title, count, children,
}) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <h3 className="text-sm font-semibold text-ui-text">{title}</h3>
      <span className="px-2 py-0.5 rounded-full text-xs bg-ui-subtle text-ui-text2">{count}</span>
    </div>
    {children}
  </div>
);

const Empty: React.FC<{ label: string }> = ({ label }) => (
  <p className="text-sm text-ui-text3 py-4 text-center border border-dashed border-ui-border2 rounded-lg">{label}</p>
);

// ─────────────────────────────── Tab views ────────────────────────────────

const OverviewTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {[
        { label: 'Stages',      value: def.stage_count },
        { label: 'Processes',   value: def.process_count },
        { label: 'Statuses',    value: def.status_count },
        { label: 'Transitions', value: def.transition_count },
        { label: 'Gates',       value: def.gate_count },
        { label: 'Hooks',       value: def.hook_count },
        { label: 'Authority Rules', value: def.authority_rule_count },
        { label: 'Entry Points', value: def.entry_points?.length ?? 0 },
      ].map(({ label, value }) => (
        <div key={label} className="bg-ui-raised border border-ui-border2 rounded-xl p-4">
          <p className="text-xs font-medium text-ui-text2 mb-1">{label}</p>
          <p className="text-2xl font-bold text-brand-dark">{value}</p>
        </div>
      ))}
    </div>

    <div className="bg-ui-raised border border-ui-border2 rounded-xl p-5 space-y-3">
      <h3 className="text-sm font-semibold text-ui-text">Definition Info</h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <div><dt className="text-ui-text3">ID</dt><dd className="font-mono text-ui-text">{def.definition_id}</dd></div>
        <div><dt className="text-ui-text3">Version</dt><dd className="text-ui-text">v{def.version}</dd></div>
        <div><dt className="text-ui-text3">Status</dt><dd><Chip label={def.status} className={STATUS_BADGE[def.status]} /></dd></div>
        <div>
          <dt className="text-ui-text3">Engagement Types</dt>
          <dd className="flex flex-wrap gap-1 mt-0.5">
            {def.engagement_types.length === 0
              ? <span className="text-ui-text3">—</span>
              : def.engagement_types.map((et) => <Chip key={et} label={et} className="bg-ui-subtle text-ui-text2" />)}
          </dd>
        </div>
        {def.published_at && (
          <div><dt className="text-ui-text3">Published</dt><dd className="text-ui-text">{new Date(def.published_at).toLocaleString()}</dd></div>
        )}
        <div><dt className="text-ui-text3">Created</dt><dd className="text-ui-text">{new Date(def.created_on).toLocaleString()}</dd></div>
        <div><dt className="text-ui-text3">Last Updated</dt><dd className="text-ui-text">{new Date(def.updated_on).toLocaleString()}</dd></div>
      </dl>
      {def.description && (
        <div>
          <p className="text-xs text-ui-text3 mb-1">Description</p>
          <p className="text-sm text-ui-text">{def.description}</p>
        </div>
      )}
    </div>
  </div>
);

const StructureTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => {
  const stageMap = Object.fromEntries(def.stages.map((s) => [s.stage_code, s]));

  return (
    <div className="space-y-8">
      <Section title="Stages" count={def.stages.length}>
        {def.stages.length === 0 ? <Empty label="No stages defined." /> : (
          <div className="space-y-2">
            {def.stages.slice().sort((a, b) => a.display_order - b.display_order).map((stage: StageDefinition) => (
              <div key={stage.stage_code} className="flex items-center gap-3 p-3 bg-ui-raised border border-ui-border2 rounded-lg">
                <span className="w-7 h-7 rounded-full bg-brand-dark/10 text-brand-dark text-xs font-bold flex items-center justify-center flex-shrink-0">
                  {stage.display_order}
                </span>
                <div>
                  <p className="text-sm font-medium text-ui-text">{stage.name}</p>
                  <p className="text-xs font-mono text-ui-text3">{stage.stage_code}</p>
                </div>
                {stage.description && (
                  <p className="text-xs text-ui-text3 ml-auto hidden md:block max-w-xs truncate">{stage.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Processes" count={def.processes.length}>
        {def.processes.length === 0 ? <Empty label="No processes defined." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ui-border">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Process</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Stage</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2 hidden md:table-cell">Order</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ui-border">
                {def.processes.slice().sort((a, b) => a.display_order - b.display_order).map((p: ProcessDefinition) => (
                  <tr key={p.process_code} className="hover:bg-ui-hover">
                    <td className="py-2 px-3">
                      <p className="font-medium text-ui-text">{p.name}</p>
                      <p className="text-xs font-mono text-ui-text3">{p.process_code}</p>
                    </td>
                    <td className="py-2 px-3 text-ui-text2">{stageMap[p.stage_code]?.name ?? p.stage_code}</td>
                    <td className="py-2 px-3 text-ui-text3 hidden md:table-cell">{p.display_order}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Statuses" count={def.statuses.length}>
        {def.statuses.length === 0 ? <Empty label="No statuses defined." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ui-border">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Status</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2 hidden sm:table-cell">Stage / Process</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Lifecycle</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2 hidden md:table-cell">Op Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ui-border">
                {def.statuses.slice().sort((a, b) => a.display_order - b.display_order).map((s: StatusDefinition) => (
                  <tr key={s.status_code} className="hover:bg-ui-hover">
                    <td className="py-2 px-3">
                      <p className="font-medium text-ui-text">{s.name}</p>
                      <p className="text-xs font-mono text-ui-text3">{s.status_code}</p>
                    </td>
                    <td className="py-2 px-3 text-ui-text2 hidden sm:table-cell">
                      <p className="text-xs">{stageMap[s.process_code]?.name ?? s.process_code}</p>
                      <p className="text-xs text-ui-text3">{s.process_code}</p>
                    </td>
                    <td className="py-2 px-3">
                      <Chip label={s.lifecycle_role} className={LIFECYCLE_BADGE[s.lifecycle_role] ?? ''} />
                    </td>
                    <td className="py-2 px-3 hidden md:table-cell">
                      <Chip label={s.operational_type} className={OP_BADGE[s.operational_type] ?? ''} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Entry Points" count={def.entry_points?.length ?? 0}>
        {!def.entry_points?.length ? <Empty label="No entry points defined." /> : (
          <div className="space-y-2">
            {def.entry_points.map((ep) => (
              <div key={ep.status_code} className="flex items-start gap-3 p-3 bg-ui-raised border border-ui-border2 rounded-lg text-sm">
                <span className="font-mono text-ui-text font-medium">{ep.status_code}</span>
                <div className="flex flex-wrap gap-1">
                  {ep.engagement_types.map((et) => (
                    <Chip key={et} label={et} className="bg-ui-subtle text-ui-text2" />
                  ))}
                </div>
                {ep.description && (
                  <span className="text-ui-text3 text-xs ml-auto hidden md:block">{ep.description}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

const TransitionsTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => {
  const statusMap = Object.fromEntries(def.statuses.map((s) => [s.status_code, s.name]));

  return (
    <Section title="Transitions" count={def.transitions.length}>
      {def.transitions.length === 0 ? <Empty label="No transitions defined." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ui-border">
                <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Transition</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">From → To</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Category</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2 hidden lg:table-cell">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ui-border">
              {def.transitions.map((t: TransitionDefinition) => (
                <tr key={t.transition_code} className="hover:bg-ui-hover">
                  <td className="py-2 px-3">
                    <p className="font-medium text-ui-text">{t.action_label}</p>
                    <p className="text-xs font-mono text-ui-text3">{t.transition_code}</p>
                    {t.description && <p className="text-xs text-ui-text3 mt-0.5">{t.description}</p>}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-ui-text">{statusMap[t.source_status_code] ?? t.source_status_code}</span>
                      <span className="text-ui-text3">→</span>
                      <span className="text-ui-text">{statusMap[t.target_status_code] ?? t.target_status_code}</span>
                    </div>
                    <div className="flex gap-1 mt-0.5">
                      <span className="font-mono text-ui-text3 text-xs">{t.source_status_code}</span>
                      <span className="text-ui-text3 text-xs">→</span>
                      <span className="font-mono text-ui-text3 text-xs">{t.target_status_code}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <Chip label={t.category} className={TRANSITION_BADGE[t.category] ?? 'bg-ui-subtle text-ui-text2'} />
                  </td>
                  <td className="py-2 px-3 hidden lg:table-cell">
                    {t.requires_reason
                      ? <span className="text-xs text-yellow-700 dark:text-yellow-400">Required</span>
                      : <span className="text-xs text-ui-text3">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
};

const GatesTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => (
  <Section title="Governance Gates" count={def.governance_gates.length}>
    {def.governance_gates.length === 0 ? <Empty label="No governance gates defined." /> : (
      <div className="space-y-3">
        {def.governance_gates.map((gate: GovernanceGate) => (
          <div key={gate.gate_code} className="bg-ui-raised border border-ui-border2 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-semibold text-ui-text font-mono">{gate.gate_code}</p>
                <p className="text-xs text-ui-text3 mt-0.5">{gate.failure_message}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Chip label={gate.scope} className="bg-ui-subtle text-ui-text2" />
                <Chip
                  label={gate.classification}
                  className={gate.classification === 'blocking'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'}
                />
              </div>
            </div>
            {gate.description && <p className="text-xs text-ui-text3 mb-2">{gate.description}</p>}
            <div className="flex flex-wrap items-center gap-3 text-xs text-ui-text3 mb-2">
              <span>Target: <span className="font-mono text-ui-text">{gate.target_ref}</span></span>
              <span>Condition: <span className="text-ui-text">{gate.condition_type}</span></span>
            </div>
            {gate.condition_config && Object.keys(gate.condition_config).length > 0 && (
              <div>
                <p className="text-xs font-medium text-ui-text2 mb-1">Config:</p>
                <pre className="text-xs text-ui-text3 bg-ui-subtle rounded p-2 overflow-x-auto">
                  {JSON.stringify(gate.condition_config, null, 2)}
                </pre>
              </div>
            )}
            {gate.required_next_action && (
              <p className="text-xs text-ui-text2 mt-2">
                <span className="font-medium">Next action: </span>{gate.required_next_action}
              </p>
            )}
          </div>
        ))}
      </div>
    )}
  </Section>
);

const HooksTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => (
  <Section title="Automation Hooks" count={def.automation_hooks.length}>
    {def.automation_hooks.length === 0 ? <Empty label="No automation hooks defined." /> : (
      <div className="space-y-3">
        {def.automation_hooks.map((hook: AutomationHook) => (
          <div key={hook.hook_code} className="bg-ui-raised border border-ui-border2 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-semibold text-ui-text font-mono">{hook.hook_code}</p>
                {hook.description && <p className="text-xs text-ui-text3 mt-0.5">{hook.description}</p>}
              </div>
              <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                <Chip label={hook.trigger_point} className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" />
                <Chip label={hook.action_type.replace(/_/g, ' ')} className="bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" />
                {hook.is_mandatory && (
                  <Chip label="mandatory" className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" />
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-ui-text3">
              <span>Trigger ref:</span>
              <span className="font-mono text-ui-text">{hook.trigger_ref}</span>
            </div>
          </div>
        ))}
      </div>
    )}
  </Section>
);

const AuthorityTab: React.FC<{ def: WorkflowDefinition }> = ({ def }) => (
  <Section title="Authority Rules" count={def.authority_rules.length}>
    {def.authority_rules.length === 0 ? <Empty label="No authority rules defined." /> : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ui-border">
              <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Rule</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Authority Type</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2 hidden sm:table-cell">Target Refs</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2">Allowed Roles</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-ui-text2 hidden md:table-cell">Assignment</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ui-border">
            {def.authority_rules.map((rule: AuthorityRule) => (
              <tr key={rule.rule_code} className="hover:bg-ui-hover">
                <td className="py-2 px-3">
                  <p className="font-mono text-xs text-ui-text">{rule.rule_code}</p>
                  {rule.description && <p className="text-xs text-ui-text3 mt-0.5">{rule.description}</p>}
                </td>
                <td className="py-2 px-3">
                  <Chip label={rule.authority_type} className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" />
                </td>
                <td className="py-2 px-3 hidden sm:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {rule.target_refs.map((ref) => (
                      <span key={ref} className="font-mono text-xs px-1.5 py-0.5 rounded bg-ui-subtle text-ui-text2">{ref}</span>
                    ))}
                  </div>
                </td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap gap-1">
                    {rule.allowed_roles.map((r) => <RolePill key={r} role={r} />)}
                  </div>
                </td>
                <td className="py-2 px-3 hidden md:table-cell">
                  <Chip label={rule.assignment_scope ?? 'any'} className="bg-ui-subtle text-ui-text2" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Section>
);

// ─────────────────────────────── Main page ────────────────────────────────

type Tab = 'overview' | 'structure' | 'transitions' | 'gates' | 'hooks' | 'authority';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'overview',    label: 'Overview' },
  { id: 'structure',   label: 'Structure' },
  { id: 'transitions', label: 'Transitions' },
  { id: 'gates',       label: 'Governance Gates' },
  { id: 'hooks',       label: 'Automation Hooks' },
  { id: 'authority',   label: 'Authority Rules' },
];

const WorkflowDefinitionPage: React.FC = () => {
  const { t } = useTranslation();
  const { definitionId = '', version = '1' } = useParams<{ definitionId: string; version: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [confirmAction, setConfirmAction] = useState<'publish' | 'retire' | 'delete' | null>(null);

  const { data, isLoading } = useGetWorkflowDefinitionQuery(
    { definition_id: definitionId, version: Number(version) },
    { skip: !definitionId },
  );

  const [publish, { isLoading: publishing }] = usePublishWorkflowDefinitionMutation();
  const [createNewVersion, { isLoading: revising }] = useCreateNewWorkflowVersionMutation();
  const [retire,  { isLoading: retiring  }] = useRetireWorkflowDefinitionMutation();
  const [remove,  { isLoading: deleting  }] = useDeleteWorkflowDefinitionMutation();
  const [revisionError, setRevisionError] = useState('');

  const def = data?.data;

  const handlePublish = async () => {
    await publish({ definition_id: definitionId, version: Number(version) });
    setConfirmAction(null);
  };

  const handleCreateNewVersion = async () => {
    setRevisionError('');
    try {
      const result = await createNewVersion({ definition_id: definitionId, version: Number(version) }).unwrap();
      navigate(`/platform/workflows/${result.data.definition_id}/${result.data.version}/edit`);
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      setRevisionError(err?.data?.message ?? 'Failed to create a new version.');
    }
  };

  const handleRetire = async () => {
    await retire({ definition_id: definitionId, version: Number(version) });
    setConfirmAction(null);
  };

  const handleDelete = async () => {
    await remove({ definition_id: definitionId, version: Number(version) });
    setConfirmAction(null);
    navigate('/platform/workflows');
  };

  if (isLoading) {
    return (
      <PageShell header={null}>
        <div className="py-20 text-center text-ui-text3 text-sm">Loading workflow definition...</div>
      </PageShell>
    );
  }

  if (!def) {
    return (
      <PageShell header={null}>
        <div className="py-20 text-center">
          <p className="text-ui-text3 text-sm">{t('platform.workflowDefinitionNotFound')}</p>
          <button
            onClick={() => navigate('/platform/workflows')}
            className="mt-4 text-sm text-brand-dark hover:underline"
          >
            ← Back to Workflows
          </button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      maxWidth="max-w-7xl"
      header={
        <div className="space-y-3">
          <button
            onClick={() => navigate('/platform/workflows')}
            className="text-xs text-ui-text3 hover:text-ui-text flex items-center gap-1"
          >
            ← Workflows
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-ui-text">{def.name}</h1>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[def.status]}`}>
                  {def.status}
                </span>
              </div>
              <p className="text-sm text-ui-text3 mt-1 font-mono">{def.definition_id} · v{def.version}</p>
              {def.description && <p className="text-sm text-ui-text2 mt-1">{def.description}</p>}
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => navigate(`/platform/workflows/${def.definition_id}/${def.version}/flowchart`)}
                className="px-4 py-2 bg-ui-subtle text-ui-text2 text-sm font-medium rounded-lg hover:bg-ui-hover transition"
              >
                View Flowchart
              </button>
              {def.status === 'draft' && (
                <>
                  <button
                    onClick={() => navigate(`/platform/workflows/${def.definition_id}/${def.version}/edit`)}
                    className="px-4 py-2 bg-ui-subtle text-ui-text2 text-sm font-medium rounded-lg hover:bg-ui-hover transition"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setConfirmAction('publish')}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition"
                  >
                    Publish
                  </button>
                  <button
                    onClick={() => setConfirmAction('delete')}
                    className="px-4 py-2 bg-ui-subtle text-ui-text2 text-sm font-medium rounded-lg hover:bg-ui-hover transition"
                  >
                    Delete Draft
                  </button>
                </>
              )}
              {def.status === 'published' && (
                <>
                  <button
                    onClick={() => void handleCreateNewVersion()}
                    disabled={revising}
                    className="px-4 py-2 bg-ui-subtle text-ui-text2 text-sm font-medium rounded-lg hover:bg-ui-hover transition disabled:opacity-50"
                  >
                    {revising ? 'Creating…' : 'Create New Version'}
                  </button>
                  <button
                    onClick={() => setConfirmAction('retire')}
                    className="px-4 py-2 bg-ui-subtle text-ui-text2 text-sm font-medium rounded-lg hover:bg-ui-hover transition"
                  >
                    Retire
                  </button>
                </>
              )}
            </div>
          </div>
          {revisionError && <p className="text-xs text-red-400">{revisionError}</p>}
        </div>
      }
    >
      {/* Tabs */}
      <div className="border-b border-ui-border mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                activeTab === id
                  ? 'border-brand-dark text-brand-dark'
                  : 'border-transparent text-ui-text2 hover:text-ui-text'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'overview'    && <OverviewTab    def={def} />}
      {activeTab === 'structure'   && <StructureTab   def={def} />}
      {activeTab === 'transitions' && <TransitionsTab def={def} />}
      {activeTab === 'gates'       && <GatesTab       def={def} />}
      {activeTab === 'hooks'       && <HooksTab       def={def} />}
      {activeTab === 'authority'   && <AuthorityTab   def={def} />}

      {/* Confirm modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-ui-card rounded-2xl shadow-xl border border-ui-border2 p-6 max-w-sm w-full">
            <h3 className="text-base font-semibold text-ui-text mb-2 capitalize">
              {confirmAction === 'delete' ? 'Delete Draft' : `${confirmAction} Workflow`}
            </h3>
            <p className="text-sm text-ui-text2 mb-4">
              {confirmAction === 'publish' && 'Publishing makes this workflow available to tenants. This action cannot be undone to draft.'}
              {confirmAction === 'retire'  && 'Retiring archives this workflow. Active engagements continue; no new engagements can adopt it.'}
              {confirmAction === 'delete'  && 'This will permanently delete this draft definition.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm rounded-lg bg-ui-subtle text-ui-text2 hover:bg-ui-hover"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction === 'publish' ? handlePublish : confirmAction === 'retire' ? handleRetire : handleDelete}
                disabled={publishing || retiring || deleting}
                className={`px-4 py-2 text-sm rounded-lg font-medium text-white transition ${
                  confirmAction === 'delete' || confirmAction === 'retire'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:opacity-50`}
              >
                {publishing || retiring || deleting ? 'Working...' : `Confirm ${confirmAction}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default WorkflowDefinitionPage;
